import React, { useState, useEffect } from "react";
import CommonModal from "../../../components/shared/modalLayout/CommonModal";
import { Row, Col } from "react-bootstrap";
import InputWithLabel from "../../../components/shared/fields/InputWithLabel";
import Button from "../../../components/shared/buttons/button";
import SelectBox from "../../../components/shared/fields/SelectBox ";
import { useGetWorkingAreasQuery } from "../../../app/globalApi";
import { toast } from "react-hot-toast";
import {
  useCreateEmployeeMutation,
  useUpdateEmployeeMutation,
} from "./employeeApi";
import moment from "moment/moment";
import { capitalize } from "../../../helpers/Utils";
import useTranslator from "../../../hooks/useTranslator";
import { validatePhoneNumber } from "../../../helpers/phoneValidation";

const initialFormData = {
  name: "",
  email: "",
  mobile: "",
  address: "",
  date_of_joining: moment().format("YYYY-MM-DDTHH:mm"),
  status: 1,
  working_area_id: null,
  role_id: null,
};

const today = moment().format("YYYY-MM-DD");

const EmployeeModal = ({ show, setShow, employee, mode }) => {
  const { _t } = useTranslator();
  const [formData, setFormData] = useState(initialFormData);
  const [roleList, setRoleList] = useState([]);
  const [formErrors, setFormErrors] = useState({});
  const { data: workingAreas } = useGetWorkingAreasQuery();
  const [createEmployee, { isLoading: isCreating }] =
    useCreateEmployeeMutation();
  const [updateEmployee, { isLoading: isUpdating }] =
    useUpdateEmployeeMutation();

  // Filter out areas where name != 'Manager'
  const filteredWorkingAreas =
    workingAreas?.data?.filter(
      (area) => area.name.toLowerCase() !== "manager"
    ) || [];

  // Initialize form data when modal opens
  useEffect(() => {
    if (show) {
      if (mode === "edit" && employee) {
        setFormData({
          ...initialFormData,
          name: employee?.name || "",
          email: employee?.email || "",
          mobile: employee?.mobile || "",
          address: employee?.address || "",
          date_of_joining: employee?.date_of_joining || "",
          status: employee?.status ?? 1,
          working_area_id: employee?.role?.working_area_id ?? null,
          role_id: employee?.role?.id ?? null,
        });
      } else {
        setFormData(initialFormData);
      }
    }
  }, [mode, employee, show]);

  // Auto-select first working area if none selected
  useEffect(() => {
    if (workingAreas?.data?.length && !formData.working_area_id) {
      setFormData((prev) => ({
        ...prev,
        working_area_id: filteredWorkingAreas[0].id,
      }));
    }
  }, [workingAreas]);

  useEffect(() => {
    if (workingAreas) {
      if (formData.working_area_id) {
        const selectedArea = filteredWorkingAreas.find(
          (area) => area.id === parseInt(formData.working_area_id)
        );
        const filteredRoles = selectedArea ? selectedArea.roles : [];
        if (filteredRoles.length > 0) {
          setFormData((prev) => ({
            ...prev,
            role_id: filteredRoles[0].id,
          }));
        }
        setRoleList(filteredRoles);
      } else {
        const selectedArea = filteredWorkingAreas[0];
        if (selectedArea) {
          setFormData((prev) => ({
            ...prev,
            working_area_id: selectedArea.id,
          }));
        }
      }
    }
  }, [workingAreas, formData?.working_area_id]);

  // Handlers
  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate phone number if provided
    const phoneValidation = validatePhoneNumber(formData.mobile);
    if (formData.mobile && !phoneValidation.isValid) {
      setFormErrors({ mobile: phoneValidation.error });
      return;
    }
    
    setFormErrors({});
    const mutation = mode === "add" ? createEmployee : updateEmployee;
    const successMessage =
      mode === "add"
        ? _t("employee_created_success", "Employee created successfully!")
        : _t("employee_updated_success", "Employee updated successfully!");
    try {
      const payload =
        mode === "add" ? formData : { id: employee.id, requestBody: formData };
      await mutation(payload).unwrap();
      handleClose();
    } catch (error) {
      // toast.error("An error occurred. Please try again later.");
      console.error("Error creating/updating employee:", error);
    }
  };

  const handleClose = () => {
    setFormErrors({});
    setShow(false);
  };

  const renderFormFields = () => (
    <>
      <Col lg={6}>
        <InputWithLabel
          label="Employee Name"
          type="text"
          name="name"
          value={formData.name}
          onChange={handleChange}
          placeholder="Employee Name..."
          required
        />
      </Col>
      <Col lg={6}>
        <InputWithLabel
          label={_t("email", "Email")}
          type="email"
          name="email"
          value={formData.email}
          onChange={handleChange}
          placeholder={_t("email", "Email")}
        />
      </Col>
      <Col lg={6}>
        <InputWithLabel
          label={_t("date_of_joining", "Date of Joining")}
          type="datetime-local"
          name="date_of_joining"
          value={formData.date_of_joining}
          onChange={handleChange}
          max={today}
          required
        />
      </Col>
      <Col lg={6}>
        <InputWithLabel
          label={_t("phone_number", "Phone Number")}
          type="mobile"
          name="mobile"
          value={formData.mobile}
          onChange={handleChange}
          placeholder="e.g. +1 202-555-0143"
          error={formErrors.mobile}
        />
      </Col>
      <Col lg={6}>
        <InputWithLabel
          label={_t("address", "Address")}
          type="text"
          name="address"
          value={formData.address}
          onChange={handleChange}
          placeholder={_t("address", "Address")}
        />
      </Col>
      <Col lg={6}>
        <SelectBox
          label={_t("status", "Status")}
          name="status"
          value={formData.status}
          onChange={handleChange}
          options={[
            { value: 1, label: _t("active", "Active") },
            { value: 0, label: _t("inactive", "Inactive") },
          ]}
          required
        />
      </Col>
      <Col lg={6}>
        <SelectBox
          label={_t("working_area", "Working Area")}
          name="working_area_id"
          value={formData.working_area_id}
          onChange={handleChange}
          options={filteredWorkingAreas.map((list) => ({
            value: list.id,
            label: _t(capitalize(list.name)),
          }))}
          disabled={mode === "edit"}
          required
        />
      </Col>

      <Col lg={6}>
        <SelectBox
          label={_t("role", "Role")}
          name="role_id"
          value={formData.role_id}
          onChange={handleChange}
          options={roleList?.map((list) => ({
            value: list.id,
            label: _t(capitalize(list.name)),
          }))}
          disabled={mode === "edit"}
          required
        />
      </Col>
    </>
  );

  const isSubmitting = isCreating || isUpdating;

  const label =
    mode === "add"
      ? isSubmitting
        ? `${_t("creating", "Creating")}...`
        : _t("add_employee", "Add Employee")
      : isSubmitting
      ? `${_t("updating", "Updating")}...`
      : _t("save_changes", "Save Changes");

  return (
    <CommonModal
      show={show}
      handleClose={handleClose}
      setShow={setShow}
      title={
        mode === "add"
          ? _t("add_employee", "Add Employee")
          : _t("edit_employee", "Edit Employee")
      }
    >
      <Row>{renderFormFields()}</Row>
      <Row>
        <Col className="text-end">
          <Button
            label={label}
            size="small"
            onClick={handleSubmit}
            isLoading={isSubmitting}
            disabled={isSubmitting}
          />
        </Col>
      </Row>
    </CommonModal>
  );
};

export default EmployeeModal;
